import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useBackground } from '../contexts/BackgroundContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { ChatBubble } from './ChatBubble';
import { ScreenIdentifierBar } from './ScreenIdentifierBar';
import ErrorReportModal from './ErrorReportModal';
import { MobileHeader } from './MobileHeader';
import { MobileNavOverlay } from './MobileNavOverlay';
import { DesktopSidebar } from './DesktopSidebar';
import { ChapterSelectionPopup } from './ChapterSelectionPopup';
import { LoginBroadcastModal } from './LoginBroadcastModal';
import { apiClient } from '../api/client';
import { getNavForRole, VIEW_CATEGORIES } from '../config/navConfig';
import { useMobileBottomNavConfig } from '../hooks/useMobileBottomNavConfig';
import { usePanelImages } from '../hooks/usePanelImages';
import { SECTION_KEYS, SECTION_LABELS, MOBILE_BREAKPOINT } from './attendee-mobile/constants';

/**
 * Inline swipe panel header for Attendee users on narrow viewports (≤768px).
 * Renders a 180px tall image banner with section name, navigation arrows, and dot indicators.
 * Images are fetched from /panel-images API with gradient fallbacks.
 */
function AttendeeSwipeHeader({ currentPath }: { currentPath: string }) {
  const { panelImages } = usePanelImages();

  // Map current route to section index
  const pathToIndex: Record<string, number> = {
    '/my-weekend': 0,
    '/guest-toolkit': 1,
    '/events': 2,
    '/marketplace': 3,
    '/announcements': 4,
    '/chapters': 5,
    '/krio-fun-facts': 6,
    '/knowledge': 7,
    '/story-builder/my-story': 8,
    '/settings': 9,
  };
  const currentIndex = pathToIndex[currentPath] ?? 0;
  const sectionKey = SECTION_KEYS[currentIndex];
  const sectionName = SECTION_LABELS[currentIndex] ?? 'My Weekend';
  const imageUrl = panelImages[sectionKey] ?? '';

  return (
    <div style={swipeStyles.wrapper}>
      <div style={{
        ...swipeStyles.panel,
        backgroundImage: imageUrl ? `url(${imageUrl})` : `linear-gradient(135deg, #1a6b3c, #2dd4bf)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}>
        <div style={swipeStyles.overlay}>
          <h2 style={swipeStyles.title}>{sectionName}</h2>
        </div>
      </div>
      {/* Dot indicators */}
      <div style={swipeStyles.dots}>
        {SECTION_KEYS.map((_, i) => (
          <span
            key={i}
            style={{
              ...swipeStyles.dot,
              ...(i === currentIndex ? swipeStyles.dotActive : {}),
            }}
          />
        ))}
      </div>
    </div>
  );
}

const swipeStyles: Record<string, React.CSSProperties> = {
  wrapper: { width: '100%' },
  panel: {
    position: 'relative',
    width: '100%',
    height: '180px',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: '#fff',
    fontSize: '1.4rem',
    fontWeight: 700,
    textAlign: 'center' as const,
    padding: '0 32px',
    margin: 0,
    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 0',
    backgroundColor: '#fff',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#d1d5db',
    transition: 'background-color 0.2s',
  },
  dotActive: {
    backgroundColor: '#1a6b3c',
    transform: 'scale(1.2)',
  },
};

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { isDark, collapsedViews, toggleViewCollapsed } = usePreferences();
  const { backgroundUrl } = useBackground();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const unreadRef = useRef(0);

  // Live Help Desk: is this user staff, and how many open requests?
  // Drives showing the "Guest Help Desk" nav item (with an open-count badge) to
  // team members who are below Organizer, on BOTH mobile and desktop menus, so
  // they can monitor new requests from their phones.
  const [isHelpDeskStaff, setIsHelpDeskStaff] = useState(false);
  const [helpDeskOpenCount, setHelpDeskOpenCount] = useState(0);

  // Compute role-filtered navigation views
  const baseNavViews = useMemo(() => getNavForRole(user?.role || ''), [user?.role]);

  // Check help-desk staff authorization once (per user)
  useEffect(() => {
    let cancelled = false;
    if (!user) { setIsHelpDeskStaff(false); return; }
    apiClient<{ isStaff: boolean }>('/help-desk/admin/authorization')
      .then((res) => { if (!cancelled) setIsHelpDeskStaff(!!res.isStaff); })
      .catch(() => { if (!cancelled) setIsHelpDeskStaff(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Poll open-request count while staff (so the badge stays fresh for monitoring)
  useEffect(() => {
    if (!isHelpDeskStaff) { setHelpDeskOpenCount(0); return; }
    let cancelled = false;
    async function fetchCount() {
      try {
        // Endpoint returns a raw number (count of Open + InProgress inquiries)
        const data = await apiClient<number>('/help-desk/admin/open-count');
        if (!cancelled) setHelpDeskOpenCount(typeof data === 'number' ? data : 0);
      } catch { /* non-critical */ }
    }
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isHelpDeskStaff]);

  // Augment nav: ensure the Guest Help Desk item is present for staff (incl.
  // below-Organizer) and carries the open-request count as a badge.
  const navViews = useMemo(() => {
    if (!isHelpDeskStaff) return baseNavViews;
    const badge = helpDeskOpenCount > 0 ? helpDeskOpenCount : undefined;
    const helpDeskItem = {
      path: '/help-desk',
      label: 'Guest Help Desk',
      icon: '🪘',
      viewId: 'overview' as const,
      badge,
    };
    let injected = false;
    const next = baseNavViews.map((cat) => {
      const existingIdx = cat.items.findIndex((i) => i.path === '/help-desk');
      if (existingIdx >= 0) {
        injected = true;
        const items = [...cat.items];
        items[existingIdx] = { ...items[existingIdx], badge };
        return { ...cat, items };
      }
      return cat;
    });
    if (injected) return next;
    // Item not present (below-Organizer staff) — add it to the Overview category.
    const overviewIdx = next.findIndex((c) => c.category.id === 'overview');
    if (overviewIdx >= 0) {
      const cat = next[overviewIdx];
      next[overviewIdx] = { ...cat, items: [...cat.items, helpDeskItem] };
      return next;
    }
    // Overview category was dropped (empty for this role, e.g. a Delegate) —
    // recreate it so the Help Desk item still shows. Insert it at the top.
    const overviewCategory = VIEW_CATEGORIES.find((c) => c.id === 'overview');
    if (overviewCategory) {
      return [{ category: overviewCategory, items: [helpDeskItem] }, ...next];
    }
    return next;
  }, [baseNavViews, isHelpDeskStaff, helpDeskOpenCount]);

  // useMediaQuery returns true when viewport width ≤ 1023px (i.e., < 1024px)
  const isMobile = useMediaQuery(1023);

  const { enabled: bottomNavEnabled } = useMobileBottomNavConfig();

  // Close MobileNavOverlay automatically when viewport crosses 768px threshold
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  // Poll for unread notifications and show toast on new ones (single poller for the whole app)
  useEffect(() => {
    async function fetchUnread() {
      try {
        const data = await apiClient<{ unreadCount: number }>('/announcements/unread-count');
        const newCount = data.unreadCount;
        if (newCount > unreadRef.current && unreadRef.current > 0) {
          setShowToast(true);
          setTimeout(() => setShowToast(false), 6000);
        }
        unreadRef.current = newCount;
      } catch {
        // Non-critical
      }
    }
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Reduced from 15s to 30s
    return () => clearInterval(interval);
  }, []);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  function handleMenuToggle() {
    setMobileMenuOpen((prev) => !prev);
  }

  function handleMenuClose() {
    setMobileMenuOpen(false);
  }

  // Mobile layout (< 768px): MobileHeader + MobileNavOverlay + full-width main
  if (isMobile) {
    return (
      <div style={layoutStyles.wrapper}>
        <MobileHeader onMenuToggle={handleMenuToggle} isMenuOpen={mobileMenuOpen} />
        <MobileNavOverlay
          isOpen={mobileMenuOpen}
          onClose={handleMenuClose}
          navViews={navViews}
          collapsedViews={collapsedViews}
          onToggleView={toggleViewCollapsed}
          user={user}
          onLogout={handleLogout}
        />
        <main style={{
          ...layoutStyles.mainMobile,
          ...(bottomNavEnabled ? { paddingBottom: '60px' } : {}),
          ...(isDark ? { backgroundColor: '#1a1a2e', color: '#e0e0e0' } : {}),
          ...(backgroundUrl ? {
            backgroundImage: `url(${backgroundUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          } : {}),
        }}>
          {showToast && (
            <div style={layoutStyles.toast} onClick={() => { setShowToast(false); navigate('/announcements'); }}>
              🔔 You have new notifications! Tap to view.
            </div>
          )}
          {children}
          <ChatBubble />
          <ErrorReportModal isOpen={showBugModal} onClose={() => setShowBugModal(false)} />
        </main>
        <ChapterSelectionPopup />
        <LoginBroadcastModal />
      </div>
    );
  }

  // Desktop layout (≥ 768px): DesktopSidebar + main with 260px margin
  return (
    <div style={layoutStyles.wrapper}>
      <DesktopSidebar
        user={user}
        navViews={navViews}
        collapsedViews={collapsedViews}
        onToggleView={toggleViewCollapsed}
        onLogout={handleLogout}
      />
      <main style={{
        ...layoutStyles.main,
        ...(isDark ? { backgroundColor: '#1a1a2e', color: '#e0e0e0' } : {}),
      }}>
        {showToast && (
          <div style={layoutStyles.toast} onClick={() => { setShowToast(false); navigate('/announcements'); }}>
            🔔 You have new notifications! Click to view.
          </div>
        )}
        {children}
        <ChatBubble />
        <ScreenIdentifierBar onReportBug={() => setShowBugModal(true)} />
        <ErrorReportModal isOpen={showBugModal} onClose={() => setShowBugModal(false)} />
      </main>
      <ChapterSelectionPopup />
      <LoginBroadcastModal />
    </div>
  );
}

const layoutStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
  },
  main: {
    flex: 1,
    marginLeft: '260px',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  mainMobile: {
    flex: 1,
    marginLeft: 0,
    paddingTop: '56px',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  toast: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 999,
    backgroundColor: '#e53935',
    color: '#fff',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center' as const,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(229,57,53,0.3)',
    animation: 'pulse-badge 2s infinite',
  },
};
